import {
  CredentialFingerprint,
  matchesCurrentEnvironment,
} from "./credentialFingerprint";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { useGeoblock } from "../../hooks/api/geoblock/useGeoblock";
import { SettingsContext } from "../SettingsContext";
import useWallet from "../../hooks/wallet/useWallet";
import {
  AuthInfoList,
  IAuthInfo,
  IDecryptedAuthInfo,
  IEncryptedAuthInfo,
} from "../../interfaces/AuthInfo";
import { decryptEncryptedAccountInfo } from "../../utils/encryption/accountPasswordEncryption";
import { AccountStateEnum, AuthInfoMap } from "./types";
import { getAccountKey, getAuthInfoStorageKey } from "./helpers";

/**
 * localStorage is user-writable and survives across app versions, so its
 * contents are untrusted input. An unguarded JSON.parse throws during the
 * hydration effect and leaves a blank page that only clearing storage can fix.
 * A malformed blob is treated as no credential, letting the normal connect
 * flow re-register.
 */
const safeParseAuthInfo = (raw: string | null): AuthInfoList => {
  if (!raw) return {} as AuthInfoList;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as AuthInfoList)
      : ({} as AuthInfoList);
  } catch {
    return {} as AuthInfoList;
  }
};

export function useAuthDerivation() {
  const [authInfoList, setAuthInfoList] = useState<AuthInfoList>({});
  // TODO: - Use imported account if available instead of from wallet
  const [importedAccountAddress, setImportedAccount] = useState<string>();
  // Trade-as-subaccount: when set (and the sub's authInfo is registered), the whole read layer
  // (account/apiKey/apiSecret/signingKey) follows the subaccount. No-op when undefined.
  const [activeSubaccount, setActiveSubaccount] = useState<string | undefined>();
  const { account: walletAccount, deactivate } = useWallet();
  const { data: geoblockData } = useGeoblock();

  // Safety: never carry a subaccount selection across wallet (master) changes or disconnects.
  useEffect(() => {
    setActiveSubaccount(undefined);
  }, [walletAccount]);

  // password to unlock the CURRENT account
  const [localPassword, setLocalPassword] = useState<string>();
  const [showInvalidRefModal, setShowInvalidRefModal] =
    useState<boolean>(false);

  const isGeoblocked = useMemo(
    () => geoblockData?.restricted,
    [geoblockData?.restricted]
  );

  // Seed the direction (up/down) color scheme from the user's region on first
  // load — East-Asian markets (CN/TW/HK/JP/KR) default to red-up/green-down.
  // No-ops once the user has chosen a scheme (persisted in localStorage).
  const { seedDirectionSchemeFromCountry } = useContext(SettingsContext);
  useEffect(() => {
    if (geoblockData?.country) {
      seedDirectionSchemeFromCountry(geoblockData.country);
    }
  }, [geoblockData?.country, seedDirectionSchemeFromCountry]);

  const storeAuthInfoToLocalStorage = useCallback((info: AuthInfoList) => {
    window.localStorage.setItem(
      getAuthInfoStorageKey(),
      JSON.stringify(info)
    );
  }, []);

  const getRawAccountAuthInfo = useCallback(() => {
    const acc = activeSubaccount || importedAccountAddress || walletAccount;
    if (!authInfoList || !acc) {
      return undefined;
    }

    const authInfoMap = authInfoList as AuthInfoMap;
    const accKey = getAccountKey(acc);
    const matchedEntry = Object.entries(authInfoMap).find(
      ([key, info]) =>
        key.toLowerCase() === accKey || getAccountKey(info.account) === accKey
    );
    const storedKey = matchedEntry?.[0];
    const authInfo = matchedEntry?.[1] as IAuthInfo | undefined;
    if (!authInfo) {
      return undefined;
    }

    // A credential minted for another chain/backend is unusable here: its
    // signing key was registered under a different EIP-712 domain, so every
    // order it signs is rejected as an invalid signature. Treat it as absent so
    // accountSigningKeyState reports REQUIRE_REGISTER_SIGNING and the normal
    // connect flow re-registers, rather than looping on a key that can never work.
    if (!matchesCurrentEnvironment(authInfo as Partial<CredentialFingerprint>)) {
      return undefined;
    }

    const account = authInfo.account || acc;
    const normalizedAccountKey = getAccountKey(account);
    const normalizedAuthInfo = {
      ...authInfo,
      account,
    };

    if (
      normalizedAccountKey &&
      (storedKey !== normalizedAccountKey || !authInfo.account)
    ) {
      const newAuthInfo: AuthInfoMap = { ...authInfoMap };
      if (storedKey && storedKey !== normalizedAccountKey) {
        delete newAuthInfo[storedKey];
      }
      newAuthInfo[normalizedAccountKey] = normalizedAuthInfo;
      storeAuthInfoToLocalStorage(newAuthInfo as AuthInfoList);
    }

    return normalizedAuthInfo;
  }, [
    activeSubaccount,
    authInfoList,
    importedAccountAddress,
    storeAuthInfoToLocalStorage,
    walletAccount,
  ]);

  const decryptedAccountAuthInfo = useMemo(() => {
    const authInfo = getRawAccountAuthInfo();
    if (!authInfo) {
      return undefined;
    }

    // Return decrypted auth info if it is encrypted
    if (authInfo.encrypted && localPassword) {
      const { decryptedInfo } = decryptEncryptedAccountInfo(
        authInfo,
        localPassword
      );
      // Spread the properties into a new object to ensure a new reference is created
      return decryptedInfo
        ? ({ ...decryptedInfo } as IDecryptedAuthInfo)
        : undefined;
    }

    // Return a new object with the properties of authInfo
    return { ...authInfo } as IDecryptedAuthInfo | IEncryptedAuthInfo;
  }, [getRawAccountAuthInfo, localPassword]);

  const accountApiKeyState = useMemo(() => {
    // TODO: Load from wallet if required
    if (!walletAccount && !importedAccountAddress) {
      return AccountStateEnum.REQUIRE_CONNECT;
    }

    if (!decryptedAccountAuthInfo) {
      return AccountStateEnum.REQUIRE_REGISTER_SIGNING;
    }

    const authInfo = decryptedAccountAuthInfo as IDecryptedAuthInfo;
    if (!authInfo.apiKey || !authInfo.apiSecret) {
      return AccountStateEnum.REQUIRE_REGISTER_SIGNING;
    }

    // If password protected and no password
    if (!!decryptedAccountAuthInfo?.passwordProtected && !localPassword) {
      return AccountStateEnum.REQUIRE_PASSWORD;
    }

    return AccountStateEnum.OK;
  }, [
    decryptedAccountAuthInfo,
    importedAccountAddress,
    localPassword,
    walletAccount,
  ]);

  const accountSigningKeyState = useMemo(() => {
    // TODO: Load from wallet if required
    if (!walletAccount && !importedAccountAddress) {
      return AccountStateEnum.REQUIRE_CONNECT;
    }

    if (!decryptedAccountAuthInfo) {
      return AccountStateEnum.REQUIRE_REGISTER_SIGNING;
    }

    const authInfo = decryptedAccountAuthInfo as IDecryptedAuthInfo;

    // you cannot perform /POST actions without api or signing key
    if (!authInfo.signingKey || !authInfo.apiKey || !authInfo.apiSecret) {
      return AccountStateEnum.REQUIRE_REGISTER_SIGNING;
    }

    // If password protected and no password
    if (!!decryptedAccountAuthInfo?.passwordProtected && !localPassword) {
      return AccountStateEnum.REQUIRE_PASSWORD;
    }

    return AccountStateEnum.OK;
  }, [
    decryptedAccountAuthInfo,
    importedAccountAddress,
    localPassword,
    walletAccount,
  ]);

  const getImportedAccountAddress = useCallback(() => {
    const importedAcc = window.localStorage.getItem(
      LocalStorageKeyEnum.IMPORTED_ACCOUNT_ADDRESS
    );
    return importedAcc;
  }, []);

  // Whenever local password is set, update session storage with the decrypted info
  useEffect(() => {
    if (decryptedAccountAuthInfo && localPassword) {
      const accountKey = getAccountKey(decryptedAccountAuthInfo.account);
      if (!accountKey) {
        return;
      }

      let newInfo: AuthInfoList = {};
      // If local password, update session storage with the decrypted info
      newInfo = {
        [accountKey]: {
          ...decryptedAccountAuthInfo,
          encrypted: false,
          passwordProtected: false,
        } as IDecryptedAuthInfo,
      };
      sessionStorage.setItem(
        getAuthInfoStorageKey(),
        JSON.stringify(newInfo)
      );
    }
  }, [decryptedAccountAuthInfo, localPassword]);

  // When context first initializes, check if there is authInfo in localStorage
  // This is the only part when localStorage is read
  useEffect(() => {
    const localAuthInfo = window.localStorage.getItem(
      getAuthInfoStorageKey()
    );

    // Session auth info that is meant to be persisted across sessions
    const sessionAuthInfo = sessionStorage.getItem(
      getAuthInfoStorageKey()
    );

    const importedAcc = getImportedAccountAddress();

    let authInfo: AuthInfoList = {};

    // Merge both local and session auth info
    if (localAuthInfo) {
      authInfo = safeParseAuthInfo(localAuthInfo);
    }
    if (sessionAuthInfo) {
      authInfo = {
        ...authInfo,
        ...safeParseAuthInfo(sessionAuthInfo),
      };
    }

    if (authInfo) {
      setAuthInfoList(authInfo);
    }

    if (importedAcc) {
      setImportedAccount(importedAcc);
    }
  }, [getImportedAccountAddress]);

  return {
    authInfoList,
    setAuthInfoList,
    importedAccountAddress,
    setImportedAccount,
    activeSubaccount,
    setActiveSubaccount,
    walletAccount,
    deactivate,
    setLocalPassword,
    showInvalidRefModal,
    setShowInvalidRefModal,
    isGeoblocked,
    storeAuthInfoToLocalStorage,
    getRawAccountAuthInfo,
    decryptedAccountAuthInfo,
    accountApiKeyState,
    accountSigningKeyState,
    getImportedAccountAddress,
  };
}
